/**
 * Pure serializer that turns Report editor state into the PATCH body for
 * `useUpdateReportMutation`. Extracted from useReportSavePipeline so the
 * hook can stay focused on the dirty-flag derivation and the
 * save/finalize side effects.
 */

import {
  FindingStatement,
  FollowUp,
  Report as MReport,
  ReportUpdatePayload,
} from '@/models/report';
import type { TemplateSection } from '@/models/template';
import { buildFindingPayloads } from './buildFindingPayloads';

interface BuildReportUpdatePayloadArgs {
  currentReport: MReport | null;
  findings: Record<string, FindingStatement[]>;
  conclusion: { uuid?: string; value: string } | null;
  selectedFlagsPayload: { flagClass: string }[];
  followUp: FollowUp | null;
  tagsSection: TemplateSection | undefined;
  status: 'DRAFT' | 'PRELIMINARY' | 'FINAL';
  includeStatus: boolean;
}

export function buildReportUpdatePayload({
  currentReport,
  findings,
  conclusion,
  selectedFlagsPayload,
  followUp,
  tagsSection,
  status,
  includeStatus,
}: BuildReportUpdatePayloadArgs): ReportUpdatePayload | null {
  if (!currentReport?.uuid) return null;

  const measurementPayloads = currentReport.measurements
    // A cleared MANUAL row is kept so its AI/SR readings stay disregarded (R20).
    .filter(
      (mv) =>
        !!mv.uuid ||
        !!mv.annotation ||
        mv.value !== null ||
        mv.valueSource === 'MANUAL'
    )
    .map((mv) => ({
      uuid: mv.uuid,
      canonicalMeasurement: mv.canonicalMeasurement,
      value: mv.value,
      unit: mv.unit,
      // null must reach the backend to clear a stale MANUAL flag (AP-613).
      valueSource: mv.valueSource ?? null,
      annotation: mv.annotation,
    }));

  const conclusionPayload = conclusion?.value?.trim()
    ? { uuid: conclusion.uuid, value: conclusion.value }
    : null;

  const deletedAIPayload = (currentReport.aiMeasurements ?? [])
    .filter((ai) => ai.isDeleted)
    .map((ai) => ({ uuid: ai.uuid, isDeleted: true }));

  return {
    uuid: currentReport.uuid,
    conclusion: conclusionPayload,
    measurements: measurementPayloads,
    findings: buildFindingPayloads(currentReport, findings),
    flags: selectedFlagsPayload,
    followUp,
    ...(tagsSection ? { tags: currentReport.tags } : {}),
    ...(deletedAIPayload.length ? { aiMeasurements: deletedAIPayload } : {}),
    ...(includeStatus ? { status } : {}),
  };
}
